import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Inbox, RefreshCw, Send, ShieldCheck, Trash2, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { Drawer } from '@/components/ui/Drawer';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { TabFilter } from '@/components/ui/TabFilter';
import { useCustomers } from '@/hooks/useCustomers';
import {
  useDiscardNotification,
  useNotifications,
  useSendNotification,
  type NotificationsFilter,
} from '@/hooks/useMail';
import { isTestModeActive, useMailSettings } from '@/hooks/useMailSettings';
import { formatDateTime, mailTriggerLabel, notificationStatusLabel } from '@/lib/format';
import type {
  MailNotification,
  MailNotificationStatus,
  MailRedirectSettings,
} from '@/types/api';

/**
 * /notifications - Postausgang.
 *
 * Alle Kundenmails laufen als MailNotification durch diese Liste: Entwürfe
 * (Automatik aus, oder maintenance_due-Ankuendigungen) können hier geprüft,
 * gesendet oder verworfen werden. Die Sende-Sicht (wohin geht der nächste
 * Versand?) kommt aus dem AKTUELLEN Testmodus (useMailSettings); die
 * persistierten `effective_recipients` sind nur Historie des letzten
 * Versands.
 */

export const Route = createFileRoute('/notifications/')({
  component: NotificationsPage,
});

type StatusTab = 'draft' | 'sent' | 'failed' | 'all';

function NotificationsPage(): JSX.Element {
  const [tab, setTab] = useState<StatusTab>('draft');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [selected, setSelected] = useState<MailNotification | null>(null);

  const filter: NotificationsFilter = useMemo(() => {
    const f: NotificationsFilter = { limit: 100 };
    if (tab !== 'all') f.status = tab;
    if (customerId) f.customerId = customerId;
    return f;
  }, [tab, customerId]);

  const notifications = useNotifications(filter);
  const customers = useCustomers({ limit: 200 });
  const mailSettings = useMailSettings();
  const testMode = mailSettings.data;

  const meta = notifications.data?.meta;
  const rows = notifications.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Postausgang</h1>
          <p className="text-muted">
            Kundenmails prüfen, senden oder verwerfen - inklusive Redirect-Sicht.
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className="size-4" />}
          loading={notifications.isFetching && !notifications.isLoading}
          onClick={() => void notifications.refetch()}
        >
          Aktualisieren
        </Button>
      </header>

      {isTestModeActive(testMode) && (
        <div
          role="status"
          data-testid="notifications-testmode-banner"
          className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
        >
          <TriangleAlert className="size-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            Testmodus aktiv - alle Kundenmails gehen nur an{' '}
            <strong className="font-mono">{testMode.redirect_address}</strong>.
          </span>
          <Link
            to="/settings/mail"
            className="ml-auto text-xs text-primary underline underline-offset-2 hover:no-underline"
          >
            Einstellungen
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <TabFilter<StatusTab>
          ariaLabel="Status-Filter"
          active={tab}
          onChange={setTab}
          items={[
            { id: 'draft', label: 'Entwürfe', ...countProp(meta?.draft_count) },
            { id: 'sent', label: 'Gesendet', ...countProp(meta?.sent_count) },
            { id: 'failed', label: 'Fehlgeschlagen', ...countProp(meta?.failed_count) },
            { id: 'all', label: 'Alle', ...countProp(meta?.total) },
          ]}
        />
        <Combobox
          className="w-full max-w-xs"
          label="Kunde"
          hideLabel
          ariaLabel="Nach Kunde filtern"
          value={customerId}
          onChange={setCustomerId}
          options={(customers.data?.data ?? []).map((c) => ({
            value: c.id,
            label: c.name,
          }))}
          placeholder="Alle Kunden"
        />
      </div>

      {notifications.isLoading && <SkeletonRows rows={6} cols={6} />}
      {notifications.isError && <ErrorAlert error={notifications.error} />}

      {notifications.data && rows.length === 0 && (
        <EmptyState
          icon={<Inbox className="size-8" />}
          title={emptyTitle(tab)}
          description="Mails entstehen automatisch aus Run-Ereignissen und fälligen Wartungen."
        />
      )}

      {rows.length > 0 && (
        <DataTable
          caption="Postausgang"
          columns={POSTAUSGANG_COLUMNS}
          rows={rows}
          rowKey={(n) => n.id}
          storageKey="postausgang"
          defaultPageSize={100}
          onRowClick={(n) => setSelected(n)}
          rowSelected={(n) => selected?.id === n.id}
          rowTestId={(n) => `notification-row-${n.id}`}
        />
      )}

      <NotificationDetailDrawer
        notification={selected}
        settings={testMode}
        onClose={() => setSelected(null)}
        onUpdated={setSelected}
      />
    </div>
  );
}

const POSTAUSGANG_COLUMNS: ReadonlyArray<Column<MailNotification>> = [
  {
    id: 'kunde',
    header: 'Kunde',
    className: 'font-medium',
    filterValue: (n) => n.customer_name ?? '',
    cell: (n) => n.customer_name ?? '–',
  },
  {
    id: 'betreff',
    header: 'Betreff',
    className: 'max-w-[28rem]',
    filterValue: (n) => n.subject,
    cell: (n) => (
      <span className="block truncate" title={n.subject}>
        {n.subject}
      </span>
    ),
  },
  {
    id: 'ausloeser',
    header: 'Auslöser',
    filter: 'select',
    filterValue: (n) => {
      const label = mailTriggerLabel(n.trigger);
      return label === '–' ? '' : label;
    },
    cell: (n) => <Badge tone="neutral">{mailTriggerLabel(n.trigger)}</Badge>,
  },
  {
    id: 'empfaenger',
    header: 'Empfänger',
    className: 'text-muted',
    filterValue: (n) => n.recipients.join(' '),
    cell: (n) => recipientsShort(n.recipients),
  },
  {
    id: 'status',
    header: 'Status',
    filter: 'select',
    filterValue: (n) => {
      const label = notificationStatusLabel(n.status);
      return label === '–' ? '' : label;
    },
    cell: (n) => <NotificationStatusBadge status={n.status} />,
  },
  {
    id: 'zeit',
    header: 'Zeit',
    className: 'text-muted',
    filterValue: (n) => {
      const label = formatDateTime(n.sent_at ?? n.created_at);
      return label === '–' ? '' : label;
    },
    cell: (n) => <RelativeTime value={n.sent_at ?? n.created_at} />,
  },
];

/** exactOptionalPropertyTypes: `count` nur setzen, wenn ein Wert da ist. */
function countProp(count: number | undefined): { count?: number } {
  return count == null ? {} : { count };
}

function emptyTitle(tab: StatusTab): string {
  switch (tab) {
    case 'draft':
      return 'Keine Entwürfe offen';
    case 'sent':
      return 'Noch keine Mails gesendet';
    case 'failed':
      return 'Keine fehlgeschlagenen Mails';
    default:
      return 'Postausgang ist leer';
  }
}

function recipientsShort(recipients: string[]): string {
  if (recipients.length === 0) return '–';
  const first = recipients[0] ?? '';
  return recipients.length === 1 ? first : `${first} +${recipients.length - 1}`;
}

export function NotificationStatusBadge({
  status,
}: {
  status: MailNotificationStatus;
}): JSX.Element {
  const tone =
    status === 'sent'
      ? ('success' as const)
      : status === 'failed'
        ? ('danger' as const)
        : status === 'draft'
          ? ('primary' as const)
          : ('neutral' as const);
  return <Badge tone={tone}>{notificationStatusLabel(status)}</Badge>;
}

// --- Detail-Drawer ---------------------------------------------------------

interface NotificationDetailDrawerProps {
  notification: MailNotification | null;
  /** AKTUELLER Testmodus-Zustand (bereits auf der Seite geladen). */
  settings: MailRedirectSettings | undefined;
  onClose: () => void;
  /** Nach Senden: Drawer zeigt den neuen Stand (sent/failed). */
  onUpdated: (n: MailNotification) => void;
}

function NotificationDetailDrawer({
  notification,
  settings,
  onClose,
  onUpdated,
}: NotificationDetailDrawerProps): JSX.Element {
  const send = useSendNotification();
  const discard = useDiscardNotification();
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  const n = notification;
  const canSend = n != null && (n.status === 'draft' || n.status === 'failed');
  // Sende-Sicht IMMER aus dem AKTUELLEN Testmodus ableiten: die persistierten
  // effective_recipients stammen vom letzten Versuch und sind nach einem
  // Testmodus-Wechsel veraltet (bei Entwürfen sogar leer).
  const testModeAktiv = isTestModeActive(settings);
  const expectedRecipients: string[] =
    n == null ? [] : testModeAktiv ? [settings.redirect_address] : n.recipients;
  // Verlässt diese Mail wirklich das Haus? Genau daran hängt, wie deutlich
  // der Bestätigungsdialog wird - nicht am Testmodus-Schalter allein, denn
  // ohne Empfänger geht ohnehin nichts raus.
  const gehtAnKunden = !testModeAktiv && expectedRecipients.length > 0;
  // Historie: ging der letzte (tatsächliche) Versand an einen Redirect?
  const historyRedirect =
    n != null &&
    n.effective_recipients.length > 0 &&
    JSON.stringify(n.effective_recipients) !== JSON.stringify(n.recipients);

  const onConfirmSend = (): void => {
    if (!n) return;
    send.mutate(n.id, {
      onSuccess: (updated) => {
        setConfirmSendOpen(false);
        onUpdated(updated);
      },
    });
  };

  const onDiscard = (): void => {
    if (!n) return;
    discard.mutate(n.id, { onSuccess: onClose });
  };

  return (
    <Drawer
      open={n != null}
      onClose={onClose}
      title={n?.subject ?? 'Mail'}
      description={
        n ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            <NotificationStatusBadge status={n.status} />
            <Badge tone="neutral">{mailTriggerLabel(n.trigger)}</Badge>
            {n.template_code && (
              <span className="font-mono text-xs">{n.template_code}</span>
            )}
          </span>
        ) : undefined
      }
      widthClass="max-w-2xl"
      footer={
        n && (n.status === 'draft' || n.status === 'failed') ? (
          <>
            <Button
              variant="secondary"
              leftIcon={<Trash2 className="size-4" />}
              loading={discard.isPending}
              onClick={onDiscard}
            >
              Verwerfen
            </Button>
            <Button
              leftIcon={<Send className="size-4" />}
              onClick={() => setConfirmSendOpen(true)}
            >
              {n.status === 'failed' ? 'Erneut senden' : 'Jetzt senden'}
            </Button>
          </>
        ) : undefined
      }
    >
      {n && (
        <div className="flex flex-col gap-4 text-sm">
          {send.isError && <ErrorAlert error={send.error} />}
          {discard.isError && <ErrorAlert error={discard.error} />}

          {n.status === 'failed' && n.error && (
            <div
              role="alert"
              className="rounded-md border border-danger/30 bg-danger/5 p-3 text-danger"
            >
              <p className="font-medium">Versand fehlgeschlagen</p>
              <p className="mt-1 break-words">{n.error}</p>
            </div>
          )}

          <dl className="grid grid-cols-3 gap-2">
            <dt className="text-muted">Kunde</dt>
            <dd className="col-span-2">
              {n.customer_id ? (
                <Link
                  to="/customers/$id"
                  params={{ id: n.customer_id }}
                  className="text-primary hover:underline"
                >
                  {n.customer_name ?? n.customer_id}
                </Link>
              ) : (
                (n.customer_name ?? '–')
              )}
            </dd>
            {n.run_id && (
              <>
                <dt className="text-muted">Run</dt>
                <dd className="col-span-2">
                  <Link
                    to="/runs/$id"
                    params={{ id: n.run_id }}
                    className="font-mono text-xs text-primary hover:underline"
                  >
                    {n.run_id}
                  </Link>
                </dd>
              </>
            )}
            <dt className="text-muted">Erstellt</dt>
            <dd className="col-span-2">{formatDateTime(n.created_at)}</dd>
            <dt className="text-muted">Gesendet</dt>
            <dd className="col-span-2">{formatDateTime(n.sent_at)}</dd>
          </dl>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">
              Empfänger (Kunde)
            </h3>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {n.recipients.length === 0 && <span className="text-muted">–</span>}
              {n.recipients.map((r) => (
                <Badge key={r} tone="neutral" className="font-mono">
                  {r}
                </Badge>
              ))}
            </div>
          </div>

          {canSend && (
            <div data-testid="notification-next-send">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">
                Nächster Versand geht an
              </h3>
              {testModeAktiv && (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-warning">
                  <TriangleAlert className="size-3.5" aria-hidden="true" />
                  Testmodus aktiv - der Versand geht NICHT an die Kunden-Adressen.
                </p>
              )}
              <div className="mt-1 flex flex-wrap gap-1.5">
                {expectedRecipients.length === 0 && <span className="text-muted">–</span>}
                {expectedRecipients.map((r) => (
                  <Badge
                    key={r}
                    tone={testModeAktiv ? 'warning' : 'success'}
                    className="font-mono"
                  >
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {n.effective_recipients.length > 0 && n.status !== 'draft' && (
            <div data-testid="notification-send-history">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">
                {n.status === 'sent'
                  ? 'Versand ging an'
                  : 'Beim letzten Versuch ging es an'}
              </h3>
              {historyRedirect && (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted">
                  <TriangleAlert className="size-3.5" aria-hidden="true" />
                  Beim Versand war ein Redirect aktiv - die Mail ging NICHT an die
                  Kunden-Adressen.
                </p>
              )}
              <div className="mt-1 flex flex-wrap gap-1.5">
                {n.effective_recipients.map((r) => (
                  <Badge key={r} tone="neutral" className="font-mono">
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">
              Nachricht
            </h3>
            <div className="mt-1 rounded-md border border-border bg-surface-2/40 p-3">
              <p className="font-medium">{n.subject}</p>
              {n.body_html ? (
                <>
                  {/* sandbox="" (ohne allow-scripts): die Vorschau darf nichts
                      ausführen und nicht navigieren. */}
                  <iframe
                    title="HTML-Vorschau"
                    data-testid="notification-html-frame"
                    srcDoc={n.body_html}
                    sandbox=""
                    className="mt-2 h-96 w-full rounded-md border border-border bg-white"
                  />
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted">
                      Text-Teil anzeigen (wird als Fallback mitgesendet)
                    </summary>
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-text">{n.body}</pre>
                  </details>
                </>
              ) : (
                <pre className="mt-2 whitespace-pre-wrap font-sans text-text">{n.body}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={confirmSendOpen}
        onClose={() => setConfirmSendOpen(false)}
        title={
          gehtAnKunden ? 'An den Kunden senden?' : 'Mail jetzt senden?'
        }
        {...(n
          ? {
              description: gehtAnKunden
                ? `Diese Mail geht an ${n.customer_name ?? 'den Kunden'} - nicht an dich.`
                : `Testmodus aktiv: der Versand geht an ${settings?.redirect_address ?? 'die Testadresse'}.`,
            }
          : {})}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmSendOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant={gehtAnKunden ? 'danger' : 'primary'}
              leftIcon={<Send className="size-4" />}
              loading={send.isPending}
              onClick={onConfirmSend}
              data-testid="confirm-send"
            >
              {gehtAnKunden ? 'An Kunden senden' : 'Senden'}
            </Button>
          </>
        }
      >
        {n && (
          <div className="flex flex-col gap-3 text-sm">
            {/* Die auffällige Warnung gehört an den GEFÄHRLICHEN Fall.
                Bis 17.08.2026 war es umgekehrt: der Testmodus (Mail geht an
                die eigene Adresse) war gelb markiert, der echte Kundenversand
                gar nicht - der Klick, der wirklich nach draußen geht, sah
                damit harmloser aus als der, der es nicht tut. */}
            {gehtAnKunden ? (
              <p
                role="alert"
                data-testid="send-warn-kunde"
                className="flex items-start gap-1.5 rounded-md border border-danger/40 bg-danger/5 p-2 text-danger"
              >
                <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  Diese Mail verlässt das Haus und erreicht{' '}
                  <strong>{n.customer_name ?? 'den Kunden'}</strong>. Ein Versand
                  lässt sich nicht zurückholen.
                </span>
              </p>
            ) : (
              <p
                data-testid="send-hinweis-testmodus"
                className="flex items-start gap-1.5 rounded-md border border-border bg-surface p-2 text-muted"
              >
                <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  Testmodus aktiv - der Kunde bekommt nichts. Der Versand geht nur an{' '}
                  <strong className="font-mono">{settings?.redirect_address}</strong>.
                </span>
              </p>
            )}

            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <dt className="text-muted">Kunde</dt>
              <dd className="font-medium">{n.customer_name ?? '–'}</dd>
              <dt className="text-muted">Betreff</dt>
              <dd className="font-medium">{n.subject}</dd>
              <dt className="text-muted">Geht an</dt>
              <dd className="flex flex-wrap gap-1.5">
                {expectedRecipients.map((r) => (
                  <Badge
                    key={r}
                    tone={gehtAnKunden ? 'danger' : 'neutral'}
                    className="font-mono"
                  >
                    {r}
                  </Badge>
                ))}
              </dd>
            </dl>

            {send.isError && <ErrorAlert error={send.error} />}
          </div>
        )}
      </Dialog>
    </Drawer>
  );
}
